import type { ReactNode } from "react";
import { Timestamp } from "firebase/firestore";
import type { DiningOrder, LoungeRound, TravelPartyMember } from "../../types";
import { calcRoundDuration, formatDuration } from "../../utils/tripDisplay";

/** Format a Timestamp as a value for a <input type="datetime-local"> control. */
function toDateTimeLocal(ts: Timestamp): string {
  const d = ts.toDate();
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/**
 * Unified orders editor that handles both simple orders (dining)
 * and multi-round orders (lounge). When rounds mode is off, it's
 * a flat list of orders. When rounds mode is on, orders are grouped
 * into numbered rounds.
 *
 * Works for any interaction type: restaurant, lounge, bar, room service, etc.
 */

interface OrdersEditorProps {
  orders: DiningOrder[];
  rounds: LoungeRound[];
  useRounds: boolean;
  onChange: (data: { orders: DiningOrder[]; rounds: LoungeRound[]; useRounds: boolean }) => void;
  travelParty: Record<string, TravelPartyMember>;
  /** Who was at this activity. When set, only these members are offered as
   *  who ordered — the rest of the trip's party wasn't there. */
  participantIds?: string[];
  /** Optional node rendered next to the section label, e.g. a save indicator. */
  statusIndicator?: ReactNode;
}

function OrderRow({
  order,
  travelParty,
  participantIds,
  onChange,
  onRemove,
  onSplit,
}: {
  order: DiningOrder;
  travelParty: Record<string, TravelPartyMember>;
  participantIds?: string[];
  onChange: (field: keyof DiningOrder, value: string) => void;
  onRemove: () => void;
  /** When set, offer to move this order into a round of its own. */
  onSplit?: () => void;
}) {
  // Keep whoever this order already names, even if they've since been taken
  // off the activity, so the row doesn't silently show someone else.
  const partyEntries = Object.entries(travelParty).filter(
    ([key]) => !participantIds || participantIds.includes(key) || key === order.participantId,
  );
  return (
    <div className="order-row">
      <div className="order-row-top">
        <select
          className="form-input order-participant-select"
          value={order.participantId}
          onChange={(e) => onChange("participantId", e.target.value)}
          aria-label="Who ordered"
        >
          <option value="SHARED">Shared / Table</option>
          {partyEntries.map(([key, member]) => (
            <option key={key} value={key}>{member.displayName}</option>
          ))}
        </select>
        {onSplit && (
          <button
            type="button"
            className="btn-icon order-split-btn"
            onClick={onSplit}
            title="Came separately — give it its own round"
          >
            Split
          </button>
        )}
        <button type="button" className="btn-icon" onClick={onRemove} title="Remove">
          &times;
        </button>
      </div>
      <input
        type="text"
        className="form-input order-input"
        value={order.order}
        onChange={(e) => onChange("order", e.target.value)}
        placeholder="What they ordered..."
        aria-label="What they ordered"
      />
    </div>
  );
}

/**
 * Per-round "Placed"/"Received" time control. Unset shows a "Set now" button;
 * once set, shows an editable datetime-local input plus a clear (×) button.
 */
function RoundTimeControl({
  label,
  value,
  onSet,
}: {
  label: string;
  value?: Timestamp | null;
  onSet: (ts: Timestamp | null) => void;
}) {
  if (!value) {
    return (
      <span className="round-time">
        <span className="round-time-label">{label}</span>
        <button type="button" className="btn btn-small btn-secondary" onClick={() => onSet(Timestamp.now())}>
          Set now
        </button>
      </span>
    );
  }
  return (
    <span className="round-time">
      <span className="round-time-label">{label}</span>
      <input
        type="datetime-local"
        step="1"
        className="form-input round-time-input"
        value={toDateTimeLocal(value)}
        onChange={(e) => {
          const d = new Date(e.target.value);
          if (!isNaN(d.getTime())) onSet(Timestamp.fromDate(d));
        }}
      />
      <button type="button" className="btn-icon" onClick={() => onSet(null)} title={`Clear ${label.toLowerCase()} time`}>
        &times;
      </button>
    </span>
  );
}

export function OrdersEditor({ orders, rounds, useRounds, onChange, travelParty, participantIds, statusIndicator }: OrdersEditorProps) {
  const toggleRounds = () => {
    if (useRounds) {
      // Switching from rounds to flat: flatten all round orders into one list
      const flatOrders = rounds.flatMap((r) => r.orders);
      onChange({ orders: flatOrders, rounds: [], useRounds: false });
    } else {
      // Switching from flat to rounds: put current orders into round 1
      const newRounds = orders.length > 0 ? [{ orders }] : [];
      onChange({ orders: [], rounds: newRounds, useRounds: true });
    }
  };

  // --- Flat mode (no rounds) ---
  if (!useRounds) {
    const handleAddOrder = () => {
      onChange({
        orders: [...orders, { participantId: "SHARED", order: "" }],
        rounds,
        useRounds: false,
      });
    };

    const handleRemoveOrder = (index: number) => {
      onChange({
        orders: orders.filter((_, i) => i !== index),
        rounds,
        useRounds: false,
      });
    };

    const handleOrderChange = (index: number, field: keyof DiningOrder, value: string) => {
      onChange({
        orders: orders.map((o, i) => (i === index ? { ...o, [field]: value } : o)),
        rounds,
        useRounds: false,
      });
    };

    // One dish landed on its own clock. A flat list has nowhere to record that,
    // so splitting switches to rounds: everything ordered together stays in
    // round 1, the straggler gets round 2 and its own received time.
    const handleSplitOrder = (index: number) => {
      const split = orders[index]!;
      onChange({
        orders: [],
        rounds: [{ orders: orders.filter((_, i) => i !== index) }, { orders: [split] }],
        useRounds: true,
      });
    };

    return (
      <div className="custom-response-section">
        <div className="section-header">
          <span className="form-label">Orders{statusIndicator}</span>
          <div className="orders-header-actions">
            <button type="button" className="btn btn-small btn-secondary" onClick={toggleRounds}>
              Use Rounds
            </button>
            <button type="button" className="btn btn-small btn-secondary" onClick={handleAddOrder}>
              + Add Order
            </button>
          </div>
        </div>
        {orders.length === 0 && <p className="text-muted">No orders yet.</p>}
        {orders.map((order, i) => (
          <OrderRow
            key={i}
            order={order}
            travelParty={travelParty}
            participantIds={participantIds}
            onChange={(field, value) => handleOrderChange(i, field, value)}
            onRemove={() => handleRemoveOrder(i)}
            onSplit={orders.length > 1 ? () => handleSplitOrder(i) : undefined}
          />
        ))}
      </div>
    );
  }

  // --- Rounds mode ---
  const handleAddRound = () => {
    onChange({
      orders,
      rounds: [...rounds, { orders: [] }],
      useRounds: true,
    });
  };

  const handleRemoveRound = (ri: number) => {
    onChange({
      orders,
      rounds: rounds.filter((_, i) => i !== ri),
      useRounds: true,
    });
  };

  const handleAddOrderToRound = (ri: number) => {
    const newRounds = [...rounds];
    const round = newRounds[ri]!;
    newRounds[ri] = {
      ...round,
      orders: [...round.orders, { participantId: "SHARED", order: "" }],
    };
    onChange({ orders, rounds: newRounds, useRounds: true });
  };

  const handleRemoveOrderFromRound = (ri: number, oi: number) => {
    const newRounds = [...rounds];
    const round = newRounds[ri]!;
    newRounds[ri] = {
      ...round,
      orders: round.orders.filter((_, i) => i !== oi),
    };
    onChange({ orders, rounds: newRounds, useRounds: true });
  };

  const setRoundTime = (ri: number, field: "placedAt" | "receivedAt", value: Timestamp | null) => {
    const newRounds = [...rounds];
    newRounds[ri] = { ...newRounds[ri]!, [field]: value };
    onChange({ orders, rounds: newRounds, useRounds: true });
  };

  // Split an order out of its round: it was ordered with the rest (so it keeps
  // the round's placed time) but arrived later, and needs its own received time.
  const handleSplitRoundOrder = (ri: number, oi: number) => {
    const source = rounds[ri]!;
    const split = source.orders[oi]!;
    const newRounds = [...rounds];
    newRounds[ri] = { ...source, orders: source.orders.filter((_, i) => i !== oi) };
    newRounds.splice(ri + 1, 0, {
      orders: [split],
      ...(source.placedAt ? { placedAt: source.placedAt } : {}),
    });
    onChange({ orders, rounds: newRounds, useRounds: true });
  };

  const handleRoundOrderChange = (ri: number, oi: number, field: keyof DiningOrder, value: string) => {
    const newRounds = [...rounds];
    const round = newRounds[ri]!;
    newRounds[ri] = {
      ...round,
      orders: round.orders.map((o, i) =>
        i === oi ? { ...o, [field]: value } : o,
      ),
    };
    onChange({ orders, rounds: newRounds, useRounds: true });
  };

  return (
    <div className="custom-response-section">
      <div className="section-header">
        <span className="form-label">Rounds{statusIndicator}</span>
        <div className="orders-header-actions">
          <button type="button" className="btn btn-small btn-secondary" onClick={toggleRounds}>
            No Rounds
          </button>
          <button type="button" className="btn btn-small btn-secondary" onClick={handleAddRound}>
            + Add Round
          </button>
        </div>
      </div>
      {rounds.length === 0 && <p className="text-muted">No rounds yet.</p>}
      {rounds.map((round, ri) => (
        <div key={ri} className="round-section">
          <div className="round-header">
            <span className="round-label">Round {ri + 1}</span>
            {calcRoundDuration(round) > 0 && (
              <span className="round-duration">{formatDuration(calcRoundDuration(round))}</span>
            )}
            <div className="round-actions">
              <button type="button" className="btn btn-small btn-secondary" onClick={() => handleAddOrderToRound(ri)}>
                + Order
              </button>
              <button type="button" className="btn-icon" onClick={() => handleRemoveRound(ri)} title="Remove round">
                &times;
              </button>
            </div>
          </div>
          <div className="round-timing">
            <RoundTimeControl
              label="Placed"
              value={round.placedAt}
              onSet={(ts) => setRoundTime(ri, "placedAt", ts)}
            />
            <RoundTimeControl
              label="Received"
              value={round.receivedAt}
              onSet={(ts) => setRoundTime(ri, "receivedAt", ts)}
            />
          </div>
          {round.orders.length === 0 && <p className="text-muted">No orders in this round.</p>}
          {round.orders.map((order, oi) => (
            <OrderRow
              key={oi}
              order={order}
              travelParty={travelParty}
              participantIds={participantIds}
              onChange={(field, value) => handleRoundOrderChange(ri, oi, field, value)}
              onRemove={() => handleRemoveOrderFromRound(ri, oi)}
              onSplit={round.orders.length > 1 ? () => handleSplitRoundOrder(ri, oi) : undefined}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Read-only display of orders (flat or rounds) */
export function OrdersDisplay({
  orders,
  rounds,
  travelParty,
}: {
  orders?: DiningOrder[];
  rounds?: LoungeRound[];
  travelParty: Record<string, TravelPartyMember>;
}) {
  const partyEntries = Object.entries(travelParty);

  const getParticipantName = (id: string) => {
    if (id === "SHARED") return "Shared";
    const entry = partyEntries.find(([key]) => key === id);
    return entry ? entry[1].displayName : id;
  };

  const hasOrders = orders && orders.length > 0;
  const hasRounds = rounds && rounds.length > 0;

  if (!hasOrders && !hasRounds) return null;

  return (
    <div className="custom-response-display">
      <h4>Orders</h4>

      {/* Flat orders */}
      {hasOrders && !hasRounds && (
        <ul className="order-display-list">
          {orders.map((order, i) => (
            <li key={i} className="order-display-item">
              <span className="order-participant">{getParticipantName(order.participantId)}</span>
              <span className="order-text">{order.order}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Rounds */}
      {hasRounds && rounds.map((round, ri) => (
        <div key={ri} className="round-display">
          <span className="round-label">Round {ri + 1}</span>
          {calcRoundDuration(round) > 0 && (
            <span className="round-duration">{formatDuration(calcRoundDuration(round))}</span>
          )}
          <ul className="order-display-list">
            {round.orders.map((order, oi) => (
              <li key={oi} className="order-display-item">
                <span className="order-participant">{getParticipantName(order.participantId)}</span>
                <span className="order-text">{order.order}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
